import ExcelJS from 'exceljs';
import {
  RECETA_OUTPUT_DEFAULTS,
  RECETA_OUTPUT_PRODUCT_TYPES,
  parsePepUsoEnPtCell,
} from '../data/mockRecetasClassification';
import { cellText, parseActiveCell } from './classificationExcel';
import { getUnits } from '../services/unitsService';
import { resolveRecipeIngredientUnitCode } from './unitSelectUtils';
import {
  RECETA_MAX_DEC_DIGITS,
  RECETA_QTY_DECIMAL_FACTOR,
  RECETA_QTY_MAX,
  RECETA_QTY_MIN,
} from './recipeQuantityLimits';

/** Logs en consola del navegador (filtro DevTools: RecetasImport). Desactivar: localStorage.removeItem('RECETAS_IMPORT_DEBUG_OFF') */
function logRecetasImport(label, data) {
  if (typeof window !== 'undefined' && localStorage.getItem('RECETAS_IMPORT_DEBUG_OFF') === '1') {
    return;
  }
  if (data === undefined) {
    console.log('[RecetasImport]', label);
    return;
  }
  console.log('[RecetasImport]', label, data);
}

export const RECETA_IMPORT_COL = {
  name: 'Nombre receta',
  /** Encabezado actual: código o nombre de familia de recetas */
  familyRef: 'Familia (código o nombre)',
  /** Plantillas anteriores */
  familyCode: 'Código familia',
  active: 'Activa (Sí/No)',
  /** Encabezado de plantilla actual */
  insumo: 'Insumo (SKU o nombre)',
  /** Compatibilidad con plantillas anteriores */
  skuLegacy: 'SKU insumo',
  quantity: 'Cantidad',
  /** Solo módulo Producción (/produccion) */
  outputType: 'Tipo producto salida',
  outputQuantity: 'Cantidad salida',
  outputUnit: 'Unidad salida',
  pepUsoEnPt: 'Uso en guía PT',
};

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = HEADER_FILL;
  });
}

function normalizeSkuForLookup(s) {
  return String(s ?? '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/**
 * Índice de unidades activas (tabla units): code, symbol y name → código canónico.
 * @param {Array<{ code?: string, symbol?: string, name?: string }>} units
 */
function buildOutputUnitLookup(units) {
  const entries = [];
  const allowedLabels = [];

  for (const u of units || []) {
    const code = String(u.code ?? '').trim();
    const symbol = String(u.symbol ?? '').trim();
    const name = String(u.name ?? '').trim();
    const canonical = code || symbol || name;
    if (!canonical) continue;

    const keys = new Set();
    [code, symbol, name, canonical].forEach((k) => {
      if (k) keys.add(k.toLowerCase());
    });
    const nameLower = name.toLowerCase();
    if (nameLower === 'unidad' || nameLower === 'unidades') {
      keys.add('unidad');
      keys.add('u');
    }
    if (code.toUpperCase() === 'UN') {
      keys.add('unidad');
      keys.add('u');
    }

    entries.push({ keys, canonical: code || canonical });
    allowedLabels.push(code || symbol || name);
  }

  const uniqueLabels = [...new Set(allowedLabels)];

  const resolve = (raw) => {
    const t = String(raw ?? '').trim();
    if (!t) {
      return { ok: false, error: 'Indique unidad de salida (catálogo de unidades de medida).' };
    }
    const key = t.toLowerCase();
    for (const entry of entries) {
      if (entry.keys.has(key)) {
        return { ok: true, value: entry.canonical, matchedKey: key };
      }
    }
    return {
      ok: false,
      error: `Unidad de salida no válida: «${t}». Debe coincidir con code, símbolo o nombre de una unidad activa en Configuración.`,
      allowedPreview: uniqueLabels.slice(0, 20),
    };
  };

  const resolveDefault = () => {
    const defRaw = RECETA_OUTPUT_DEFAULTS.output_unit_of_measure;
    const hit = resolve(defRaw);
    if (hit.ok) return hit;
    if (entries.length > 0) {
      return { ok: true, value: entries[0].canonical, matchedKey: '(primera unidad activa)' };
    }
    return { ok: false, error: 'No hay unidades de medida activas en el catálogo.' };
  };

  return { entries, uniqueLabels, resolve, resolveDefault };
}

/**
 * @returns {{ ok: true, value: string } | { ok: false, error: string }}
 */
function parseOutputUnitStrict(raw, outputUnitLookup) {
  if (!outputUnitLookup?.entries?.length) {
    return {
      ok: false,
      error:
        'No se cargó el catálogo de unidades de medida. Revise Configuración › Unidades e intente de nuevo.',
    };
  }
  const trimmed = String(raw ?? '').trim();
  const res = trimmed === '' ? outputUnitLookup.resolveDefault() : outputUnitLookup.resolve(trimmed);
  logRecetasImport('resolveOutputUnit', {
    raw: trimmed || '(vacío → default)',
    ok: res.ok,
    value: res.ok ? res.value : undefined,
    matchedKey: res.ok ? res.matchedKey : undefined,
    allowedCount: outputUnitLookup.uniqueLabels.length,
    allowedPreview: outputUnitLookup.uniqueLabels.slice(0, 12),
  });
  if (!res.ok) {
    const preview = res.allowedPreview?.length
      ? ` Ejemplos: ${res.allowedPreview.join(', ')}.`
      : outputUnitLookup.uniqueLabels.length
        ? ` Unidades activas: ${outputUnitLookup.uniqueLabels.slice(0, 15).join(', ')}.`
        : '';
    return { ok: false, error: `${res.error}${preview}` };
  }
  return { ok: true, value: res.value };
}

function outputTypeLabel(value) {
  const hit = RECETA_OUTPUT_PRODUCT_TYPES.find((o) => o.value === value);
  return hit?.label || value;
}

/**
 * @returns {{ ok: true, value: string } | { ok: false, error: string }}
 */
function parseOutputProductType(raw) {
  const t = String(raw ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
  if (!t) {
    return { ok: true, value: RECETA_OUTPUT_DEFAULTS.output_product_type };
  }
  const terminado = new Set([
    'producto terminado',
    'terminado',
    'pt',
    'producto_terminado',
    'producto terminado (venta)',
  ]);
  const enProceso = new Set([
    'producto en proceso',
    'en proceso',
    'proceso',
    'pp',
    'producto_en_proceso',
    'intermedio',
  ]);
  const salidaInsumo = new Set([
    'salida como insumo',
    'salida insumo',
    'insumo',
    'salida_insumo',
    'co-producto insumo',
  ]);
  if (terminado.has(t)) {
    return { ok: true, value: 'producto_terminado' };
  }
  if (enProceso.has(t)) {
    return { ok: true, value: 'producto_en_proceso' };
  }
  if (salidaInsumo.has(t)) {
    return { ok: true, value: 'salida_insumo' };
  }
  return {
    ok: false,
    error:
      'Tipo producto salida inválido. Use «Producto terminado», «Producto en proceso» o «Salida como insumo».',
  };
}

function headerMapFromRow(row) {
  const map = {};
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const t = cellText(cell);
    if (t) map[t] = colNumber;
  });
  return map;
}

/**
 * Fila de encabezados: insumo; familía como «Familia (código o nombre)» o «Código familia».
 */
function findRecetaImportHeaderRow(sheet) {
  const must = [RECETA_IMPORT_COL.name, RECETA_IMPORT_COL.active, RECETA_IMPORT_COL.quantity];
  const maxScan = Math.min(30, sheet.rowCount || 30);
  for (let r = 1; r <= maxScan; r++) {
    const row = sheet.getRow(r);
    const map = headerMapFromRow(row);
    const insumoCol = map[RECETA_IMPORT_COL.insumo] ?? map[RECETA_IMPORT_COL.skuLegacy];
    const famCol = map[RECETA_IMPORT_COL.familyRef] ?? map[RECETA_IMPORT_COL.familyCode];
    if (insumoCol == null || famCol == null) continue;
    if (must.some((label) => map[label] == null)) continue;
    return { rowIndex: r, colMap: map, insumoCol, famCol };
  }
  return null;
}

function getLastUsedRow(sheet) {
  let last = 0;
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber > last) last = rowNumber;
  });
  return last;
}

async function loadFirstSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets[0] || null;
}

/**
 * @returns {{ byCode: Map<string, {id:number, code?:string, name?:string}>, byName: Map<string, {id:number}>, ambiguousFamilyNameKeys: Set<string> }}
 */
function buildRecetaFamilyLookupMaps(hierarchy) {
  const byCode = new Map();
  const nameCount = new Map();
  const all = [];
  for (const c of hierarchy || []) {
    for (const s of c.subcategories || []) {
      for (const f of s.families || []) {
        const fam = { id: f.id, code: f.code, name: f.name };
        all.push(fam);
        const ck = String(f.code ?? '')
          .trim()
          .toUpperCase();
        if (ck && !byCode.has(ck)) {
          byCode.set(ck, fam);
        }
        const nk = normalizeInsumoName(f.name);
        if (nk) {
          nameCount.set(nk, (nameCount.get(nk) || 0) + 1);
        }
      }
    }
  }
  const ambiguousFamilyNameKeys = new Set();
  for (const [nk, c] of nameCount.entries()) {
    if (c > 1) {
      ambiguousFamilyNameKeys.add(nk);
    }
  }
  const byName = new Map();
  for (const fam of all) {
    const nk = normalizeInsumoName(fam.name);
    if (!nk || ambiguousFamilyNameKeys.has(nk)) {
      continue;
    }
    if (!byName.has(nk)) {
      byName.set(nk, fam);
    }
  }
  return { byCode, byName, ambiguousFamilyNameKeys };
}

/**
 * @returns {{ family: { id: number, code?: string, name?: string } } | { error: string }}
 */
function resolveRecetaFamily(raw, { byCode, byName, ambiguousFamilyNameKeys }) {
  const t = String(raw ?? '').trim();
  if (!t) {
    return { error: 'Indique familia: código o nombre de la familia (clasificación de recetas).' };
  }
  const codeHit = byCode.get(t.toUpperCase());
  if (codeHit) {
    return { family: codeHit };
  }
  const nk = normalizeInsumoName(t);
  if (ambiguousFamilyNameKeys.has(nk)) {
    return {
      error:
        'Hay varias familias con el mismo nombre; use el código de familia de la columna o unifique nombres en el catálogo.',
    };
  }
  const nameHit = byName.get(nk);
  if (nameHit) {
    return { family: nameHit };
  }
  return {
    error: `Familia no encontrada: «${t}» (código o nombre exacto, como en la clasificación de recetas).`,
  };
}

function cleanInsumoCellText(raw) {
  return String(raw ?? '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Nombre de producto para búsqueda exacta (sin distinguir mayúsculas ni tildes). */
function normalizeInsumoName(s) {
  return cleanInsumoCellText(s)
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Lista del catálogo de insumos para logs y depuración en consola. */
function buildCatalogEntries(insumoProducts, { bySku, byName, ambiguousNameKeys }) {
  return (insumoProducts || []).map((p) => {
    const nameKey = normalizeInsumoName(p.name);
    const skuKey = normalizeSkuForLookup(p.sku);
    return {
      id: p.id,
      name: p.name,
      sku: p.sku ?? '',
      nameKey,
      skuKey: skuKey || null,
      unit_of_measure: p.unit_of_measure ?? '',
      indexadoPorSku: Boolean(skuKey && bySku.get(skuKey)?.id === p.id),
      indexadoPorNombre: Boolean(nameKey && !ambiguousNameKeys.has(nameKey) && byName.get(nameKey)?.id === p.id),
      nombreAmbiguo: Boolean(nameKey && ambiguousNameKeys.has(nameKey)),
    };
  });
}

/**
 * @returns {{ bySku: Map<string, any>, byName: Map<string, any>, ambiguousNameKeys: Set<string>, catalog: object[] }}
 */
function buildInsumoLookupMaps(insumoProducts) {
  const bySku = new Map();
  const byName = new Map();
  const nameCount = new Map();
  const skuList = [];

  for (const p of insumoProducts || []) {
    const k = normalizeSkuForLookup(p.sku);
    if (k) {
      skuList.push({ id: p.id, skuRaw: p.sku, skuKey: k, name: p.name });
      if (!bySku.has(k)) {
        bySku.set(k, p);
      } else {
        logRecetasImport('skuDuplicadoEnCatalogo', { skuKey: k, existingId: bySku.get(k)?.id, newId: p.id });
      }
    }
  }

  for (const p of insumoProducts || []) {
    const nk = normalizeInsumoName(p.name);
    if (!nk) continue;
    nameCount.set(nk, (nameCount.get(nk) || 0) + 1);
  }

  const ambiguousNameKeys = new Set();
  for (const [nk, c] of nameCount.entries()) {
    if (c > 1) {
      ambiguousNameKeys.add(nk);
    }
  }

  for (const p of insumoProducts || []) {
    const nk = normalizeInsumoName(p.name);
    if (!nk || ambiguousNameKeys.has(nk)) continue;
    if (!byName.has(nk)) {
      byName.set(nk, p);
    }
  }

  const nameList = (insumoProducts || [])
    .map((p) => ({
      id: p.id,
      nameRaw: p.name,
      nameKey: normalizeInsumoName(p.name),
      sku: p.sku,
    }))
    .filter((x) => x.nameKey);

  const catalog = buildCatalogEntries(insumoProducts, { bySku, byName, ambiguousNameKeys });

  if (typeof window !== 'undefined') {
    window.__RECETAS_IMPORT_CATALOGO__ = catalog;
  }

  logRecetasImport('buildInsumoLookupMaps', {
    totalProductos: (insumoProducts || []).length,
    conSku: skuList.length,
    sinSku: (insumoProducts || []).length - skuList.length,
    clavesSkuUnicas: bySku.size,
    clavesNombreUnicas: byName.size,
    nombresAmbiguos: ambiguousNameKeys.size,
    muestraSkus: skuList.slice(0, 6).map((x) => ({ skuKey: x.skuKey, skuRaw: x.skuRaw, id: x.id, name: x.name })),
    muestraNombres: nameList.slice(0, 6).map((x) => ({ nameKey: x.nameKey, nameRaw: x.nameRaw, id: x.id, sku: x.sku })),
    catalogo: catalog,
  });

  return { bySku, byName, ambiguousNameKeys, catalog };
}

/**
 * @returns {{ product: any } | { error: string }}
 */
function findSkuNearMatches(searchKey, bySku, limit = 5) {
  if (!searchKey || bySku.size === 0) return [];
  const hits = [];
  for (const catalogKey of bySku.keys()) {
    if (catalogKey === searchKey) continue;
    if (catalogKey.includes(searchKey) || searchKey.includes(catalogKey)) {
      const p = bySku.get(catalogKey);
      hits.push({ skuKey: catalogKey, id: p?.id, name: p?.name });
      if (hits.length >= limit) break;
    }
  }
  return hits;
}

function findNameNearMatches(searchKey, byName, limit = 5) {
  if (!searchKey || byName.size === 0) return [];
  const hits = [];
  for (const [nameKey, p] of byName.entries()) {
    if (nameKey === searchKey) continue;
    if (nameKey.includes(searchKey) || searchKey.includes(nameKey)) {
      hits.push({ nameKey, id: p?.id, sku: p?.sku, name: p?.name });
      if (hits.length >= limit) break;
    }
  }
  return hits;
}

/**
 * Resuelve insumo por SKU del producto o por nombre exacto (columna «Insumo (SKU o nombre)»).
 * @returns {{ product: any, matchMethod: 'sku'|'nombre' } | { error: string }}
 */
function resolveInsumoProduct(raw, { bySku, byName, ambiguousNameKeys, catalog = [] }) {
  const t = cleanInsumoCellText(raw);
  if (!t) {
    return { error: 'Indique insumo: SKU o nombre del producto (catálogo de insumos).' };
  }

  const skuKey = normalizeSkuForLookup(t);
  const nameKey = normalizeInsumoName(t);

  const bySkuHit = bySku.get(skuKey);
  const nameAmbiguous = ambiguousNameKeys.has(nameKey);
  const byNameHit = nameAmbiguous ? null : byName.get(nameKey);

  const catalogoFiltro = (catalog || []).filter((item) => {
    const q = nameKey;
    if (!q || q.length < 2) return false;
    return (
      (item.nameKey && (item.nameKey.includes(q) || q.includes(item.nameKey))) ||
      (item.skuKey && (item.skuKey.includes(skuKey) || skuKey.includes(item.skuKey)))
    );
  });

  logRecetasImport('resolveInsumo', {
    textoExcel: t,
    intentoSku: skuKey,
    intentoNombre: nameKey,
    matchPorSku: Boolean(bySkuHit),
    matchPorNombre: Boolean(byNameHit),
    nombreAmbiguo: nameAmbiguous,
    productoPorSku: bySkuHit
      ? { id: bySkuHit.id, sku: bySkuHit.sku, name: bySkuHit.name }
      : null,
    productoPorNombre: byNameHit
      ? { id: byNameHit.id, sku: byNameHit.sku, name: byNameHit.name }
      : null,
    totalSkusEnCatalogo: bySku.size,
    totalNombresEnCatalogo: byName.size,
    /** Catálogo completo usado en este import (insumos + productos en proceso). */
    catalogo: catalog,
    /** Subconjunto con nombre/SKU parecido al texto del Excel (más fácil de revisar). */
    catalogoParecido: catalogoFiltro.length > 0 ? catalogoFiltro : undefined,
  });

  if (bySkuHit && byNameHit && Number(bySkuHit.id) !== Number(byNameHit.id)) {
    return {
      error: `«${t}» coincide con el SKU de «${bySkuHit.name}» (${bySkuHit.sku || 'sin SKU'}) y con otro producto por nombre «${byNameHit.name}». Use solo SKU o solo nombre para un insumo concreto.`,
    };
  }

  if (bySkuHit) {
    return { product: bySkuHit, matchMethod: 'sku' };
  }

  if (nameAmbiguous) {
    return {
      error:
        'Hay varios insumos con el mismo nombre; escriba el SKU del producto en esta fila o unifique nombres en Productos.',
    };
  }

  if (byNameHit) {
    return { product: byNameHit, matchMethod: 'nombre' };
  }

  const nearSku = findSkuNearMatches(skuKey, bySku);
  const nearName = findNameNearMatches(nameKey, byName);
  if (nearSku.length) {
    logRecetasImport('skuCercanos', { buscado: skuKey, candidatos: nearSku });
  }
  if (nearName.length) {
    logRecetasImport('nombresCercanos', { buscado: nameKey, candidatos: nearName });
  }

  const hints = [];
  if (nearSku.length) {
    hints.push(`SKUs parecidos: ${nearSku.map((x) => x.skuKey).join(', ')}`);
  }
  if (nearName.length) {
    hints.push(`Nombres parecidos: ${nearName.map((x) => x.name).join(', ')}`);
  }
  const hintSuffix = hints.length ? ` ${hints.join('. ')}.` : '';

  return {
    error: `Insumo no encontrado: «${t}». Use el SKU o el nombre exacto del producto (insumos activos o productos en proceso).${hintSuffix}`,
  };
}

function parseQuantityStrict(raw) {
  const t = String(raw ?? '')
    .replace(/\s/g, '')
    .replace(',', '.');
  if (t === '') return { ok: false, error: 'Indique cantidad' };
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return { ok: false, error: 'Cantidad no numérica' };
  const q = Math.round(n * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
  if (q < RECETA_QTY_MIN) {
    return { ok: false, error: `La cantidad debe ser ≥ ${RECETA_QTY_MIN}` };
  }
  if (q > RECETA_QTY_MAX) {
    return { ok: false, error: `La cantidad no puede superar ${RECETA_QTY_MAX}` };
  }
  return { ok: true, value: q };
}

function formatApiError(err) {
  const d = err.response?.data;
  if (!d) return err.message || 'Error desconocido';
  if (d.message) return d.message;
  if (d.errors) {
    return Object.values(d.errors)
      .flat()
      .join('; ');
  }
  return 'Error al guardar';
}

const REQUIRED_HEADER_DESCRIPTION =
  'Familia («Familia (código o nombre)» o «Código familia»), Nombre receta, Insumo, Cantidad y Activa al final.';

/**
 * Plantilla Excel: familia, receta, [salida], insumo, cantidad, activa.
 * @param {{ includeOutputConfig?: boolean }} [options]
 */
export async function downloadRecetasImportTemplate(options = {}) {
  const { includeOutputConfig = false } = options;
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Recetas', { views: [{ state: 'frozen', ySplit: 2 }] });
  const headers = [
    RECETA_IMPORT_COL.familyRef,
    RECETA_IMPORT_COL.name,
    ...(includeOutputConfig
      ? [
          RECETA_IMPORT_COL.outputType,
          RECETA_IMPORT_COL.outputQuantity,
          RECETA_IMPORT_COL.outputUnit,
          RECETA_IMPORT_COL.pepUsoEnPt,
        ]
      : []),
    RECETA_IMPORT_COL.insumo,
    RECETA_IMPORT_COL.quantity,
    RECETA_IMPORT_COL.active,
  ];
  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value = includeOutputConfig
    ? 'Producción: 1) Familia (código o nombre), 2) Nombre receta, 3) Tipo producto salida («Producto terminado» o «Producto en proceso»; vacío = terminado), 4) Cantidad salida (vacío = 1), 5) Unidad salida (code/símbolo/nombre exacto de una unidad activa en Configuración; vacío = unidad por defecto), 6) Uso en guía PT (solo PEP: «Subreceta automática» o «Guía PEP»; vacío = valor por defecto del módulo), 7) Insumo: SKU o nombre exacto del producto, 8) Cantidad ingrediente, 9) Activa (Sí/No). Fila 2 = encabezados; desde fila 3. Familia, nombre, salida y activa se heredan en filas siguientes del mismo plato.'
    : `Orden: 1) Familia: código o nombre exacto, 2) Nombre de la receta, 3) Insumo: SKU del producto o nombre exacto (como en Productos), 4) Cantidad (hasta ${RECETA_MAX_DEC_DIGITS} decimales), 5) Activa: Sí o No (al final). Fila 2 = encabezados; datos desde fila 3. Puede dejar en blanco familia, nombre y activa en filas siguientes; se heredan.`;
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = includeOutputConfig ? 110 : 88;
  styleHeaderRow(ws.getRow(2), headers);
  const widths = includeOutputConfig
    ? [{ width: 18 }, { width: 28 }, { width: 22 }, { width: 14 }, { width: 14 }, { width: 22 }, { width: 30 }, { width: 12 }, { width: 16 }]
    : [{ width: 18 }, { width: 28 }, { width: 30 }, { width: 12 }, { width: 16 }];
  ws.columns = widths;
  if (includeOutputConfig) {
    ws.getRow(3).values = [
      'CERDO GUISADO',
      'ADOBO',
      'Producto en proceso',
      1,
      'unidad',
      'Subreceta automática',
      'SKU-INSUMO-1',
      0.5,
      'Sí',
    ];
  }
  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(
    buf,
    includeOutputConfig ? 'plantilla_importar_recetas_produccion.xlsx' : 'plantilla_importar_recetas.xlsx',
  );
}

/**
 * @param {File} file
 * @param {{ hierarchy: any[], insumoProducts: any[] }} ctx
 * @returns {Promise<
 *   | { success: false; fatalMessage: string }
 *   | {
 *       success: true;
 *       previewRows: any[];
 *       validRecipes: { displayName: string; firstRow: number; payload: object }[];
 *       recipeCount: number;
 *       ingredientLines: number;
 *       validIngredientRows: number;
 *       emptyCount: number;
 *       invalidCount: number;
 *     }
 * >}
 */
export async function analyzeRecetasImport(file, ctx) {
  const { hierarchy = [], insumoProducts = [], includeOutputConfig = false, globalFabricarSubrecetaDefault = false } = ctx || {};
  logRecetasImport('analyze:start', {
    archivo: file?.name,
    familiasJerarquia: (hierarchy || []).length,
    insumosCatalogo: (insumoProducts || []).length,
    includeOutputConfig,
  });

  let outputUnitLookup = null;
  let ingredientUnits = [];
  try {
    const unitsRes = await getUnits({ is_active: true });
    const activeUnits = unitsRes?.success && Array.isArray(unitsRes.data) ? unitsRes.data : [];
    ingredientUnits = activeUnits;
    if (includeOutputConfig) {
      outputUnitLookup = buildOutputUnitLookup(activeUnits);
      logRecetasImport('unidadesSalidaCatalogo', {
        cargadas: activeUnits.length,
        permitidas: outputUnitLookup.uniqueLabels,
      });
    }
  } catch (err) {
    logRecetasImport('unidadesCatalogo:error', err?.message || err);
  }

  const sheet = await loadFirstSheet(file);
  if (!sheet) {
    return { success: false, fatalMessage: 'El archivo no tiene hojas.' };
  }

  const found = findRecetaImportHeaderRow(sheet);
  if (!found) {
    return {
      success: false,
      fatalMessage: `No se encontró la fila de encabezados. Se requieren: ${REQUIRED_HEADER_DESCRIPTION}`,
    };
  }

  logRecetasImport('encabezados', {
    fila: found.rowIndex,
    columnas: found.colMap,
    insumoCol: found.insumoCol,
    famCol: found.famCol,
  });

  const familyLookup = buildRecetaFamilyLookupMaps(hierarchy);
  const insumoLookup = buildInsumoLookupMaps(insumoProducts);

  const { rowIndex: headerRow, colMap, insumoCol, famCol } = found;
  const m = colMap;
  const last = getLastUsedRow(sheet);

  const outputTypeCol = m[RECETA_IMPORT_COL.outputType];
  const outputQtyCol = m[RECETA_IMPORT_COL.outputQuantity];
  const outputUnitCol = m[RECETA_IMPORT_COL.outputUnit];
  const pepUsoCol = m[RECETA_IMPORT_COL.pepUsoEnPt];
  const sheetHasOutputCols =
    includeOutputConfig && outputTypeCol != null && outputQtyCol != null && outputUnitCol != null;

  let carryName = '';
  let carryFamily = '';
  let carryActive = true;
  let carryOutputType = RECETA_OUTPUT_DEFAULTS.output_product_type;
  let carryOutputQtyRaw = RECETA_OUTPUT_DEFAULTS.output_quantity;
  let carryOutputUnitRaw = RECETA_OUTPUT_DEFAULTS.output_unit_of_measure;
  let carryPepUsoRaw = '';

  const expanded = [];

  for (let r = headerRow + 1; r <= last; r++) {
    const row = sheet.getRow(r);
    const rawName = cellText(row.getCell(m[RECETA_IMPORT_COL.name]));
    const rawFam = cellText(row.getCell(famCol));
    const rawAct = cellText(row.getCell(m[RECETA_IMPORT_COL.active]));
    const rawInsumo = cellText(row.getCell(insumoCol));
    const rawQty = cellText(row.getCell(m[RECETA_IMPORT_COL.quantity]));
    const rawOutputType = sheetHasOutputCols ? cellText(row.getCell(outputTypeCol)) : '';
    const rawOutputQty = sheetHasOutputCols ? cellText(row.getCell(outputQtyCol)) : '';
    const rawOutputUnit = sheetHasOutputCols ? cellText(row.getCell(outputUnitCol)) : '';
    const rawPepUso = sheetHasOutputCols && pepUsoCol != null ? cellText(row.getCell(pepUsoCol)) : '';

    if (rawName) carryName = rawName.trim();
    if (rawFam) carryFamily = rawFam.trim();
    if (rawAct !== '') carryActive = parseActiveCell(rawAct);
    if (sheetHasOutputCols) {
      if (rawOutputType !== '') carryOutputType = rawOutputType.trim();
      if (rawOutputQty !== '') carryOutputQtyRaw = rawOutputQty;
      if (rawOutputUnit !== '') carryOutputUnitRaw = rawOutputUnit.trim();
      if (rawPepUso !== '') carryPepUsoRaw = rawPepUso.trim();
    }

    const lineEmpty =
      !rawInsumo &&
      !rawQty &&
      !rawName &&
      !rawFam &&
      rawAct === '' &&
      (!sheetHasOutputCols ||
        (rawOutputType === '' && rawOutputQty === '' && rawOutputUnit === '' && rawPepUso === ''));

    if (lineEmpty) {
      expanded.push({
        excelRow: r,
        kind: 'empty',
        effectiveName: carryName,
        effectiveFamily: carryFamily,
        effectiveActive: carryActive,
        effectiveOutputTypeRaw: carryOutputType,
        effectiveOutputQtyRaw: carryOutputQtyRaw,
        effectiveOutputUnitRaw: carryOutputUnitRaw,
        effectivePepUsoRaw: carryPepUsoRaw,
        insumo: '',
        qtyRaw: '',
      });
      continue;
    }

    expanded.push({
      excelRow: r,
      kind: 'data',
      effectiveName: carryName,
      effectiveFamily: carryFamily,
      effectiveActive: carryActive,
      effectiveOutputTypeRaw: carryOutputType,
      effectiveOutputQtyRaw: carryOutputQtyRaw,
      effectiveOutputUnitRaw: carryOutputUnitRaw,
      effectivePepUsoRaw: carryPepUsoRaw,
      insumo: rawInsumo.trim(),
      qtyRaw: rawQty,
    });
  }

  const groups = new Map();

  const previewRows = [];

  for (const line of expanded) {
    if (line.kind === 'empty') {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'empty',
        cols: {
          nombre: '',
          familia: '',
          tipoSalida: '',
          cantidadSalida: '',
          unidadSalida: '',
          activa: '',
          insumo: '',
          cantidad: '',
        },
        issue: '',
      });
      continue;
    }

    const name = line.effectiveName;
    const famCode = line.effectiveFamily;

    const colsPreview = {
      nombre: name || '—',
      familia: famCode || '—',
      tipoSalida: includeOutputConfig ? line.effectiveOutputTypeRaw || '—' : undefined,
      cantidadSalida: includeOutputConfig ? String(line.effectiveOutputQtyRaw ?? '—') : undefined,
      unidadSalida: includeOutputConfig ? line.effectiveOutputUnitRaw || '—' : undefined,
      usoEnGuiaPt: includeOutputConfig ? line.effectivePepUsoRaw || '—' : undefined,
      activa: line.effectiveActive ? 'Sí' : 'No',
      insumo: line.insumo || '—',
      cantidad: line.qtyRaw ?? '—',
    };

    if (!name) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Indique nombre de receta (primera fila del plato o columna rellena).',
      });
      continue;
    }
    if (!famCode) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Indique familia: código o nombre (clasificación de recetas).',
      });
      continue;
    }
    if (!line.insumo) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Indique insumo: SKU o nombre del producto.',
      });
      continue;
    }

    const qtyRes = parseQuantityStrict(line.qtyRaw);
    if (!qtyRes.ok) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: qtyRes.error,
      });
      continue;
    }

    const famResolved = resolveRecetaFamily(famCode, familyLookup);
    logRecetasImport('resolveFamilia', {
      fila: line.excelRow,
      textoExcel: famCode,
      ok: !famResolved.error,
      familyId: famResolved.family?.id,
      familyCode: famResolved.family?.code,
    });
    if (famResolved.error) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: famResolved.error,
      });
      continue;
    }
    const fam = famResolved.family;

    let outputConfig = null;
    if (includeOutputConfig) {
      const typeRes = parseOutputProductType(line.effectiveOutputTypeRaw);
      if (!typeRes.ok) {
        previewRows.push({
          excelRow: line.excelRow,
          status: 'invalid',
          cols: colsPreview,
          issue: typeRes.error,
        });
        continue;
      }
      const outQtyRes = parseQuantityStrict(line.effectiveOutputQtyRaw);
      if (!outQtyRes.ok) {
        previewRows.push({
          excelRow: line.excelRow,
          status: 'invalid',
          cols: { ...colsPreview, cantidadSalida: line.effectiveOutputQtyRaw ?? '—' },
          issue: `Cantidad salida: ${outQtyRes.error}`,
        });
        continue;
      }
      const outUnitRes = parseOutputUnitStrict(line.effectiveOutputUnitRaw, outputUnitLookup);
      if (!outUnitRes.ok) {
        previewRows.push({
          excelRow: line.excelRow,
          status: 'invalid',
          cols: { ...colsPreview, unidadSalida: line.effectiveOutputUnitRaw || '—' },
          issue: outUnitRes.error,
        });
        continue;
      }
      const outUnit = outUnitRes.value;
      let fabricarSubrecetaDesdePt = null;
      if (typeRes.value === 'producto_en_proceso') {
        const pepParsed = parsePepUsoEnPtCell(line.effectivePepUsoRaw);
        if (!pepParsed.ok) {
          previewRows.push({
            excelRow: line.excelRow,
            status: 'invalid',
            cols: { ...colsPreview, usoEnGuiaPt: line.effectivePepUsoRaw || '—' },
            issue: pepParsed.error,
          });
          continue;
        }
        fabricarSubrecetaDesdePt = pepParsed.skip ? globalFabricarSubrecetaDefault : pepParsed.value;
      }
      outputConfig = {
        output_product_type: typeRes.value,
        output_quantity: outQtyRes.value,
        output_unit_of_measure: outUnit,
        fabricar_subreceta_desde_pt: fabricarSubrecetaDesdePt,
        _previewTypeLabel: outputTypeLabel(typeRes.value),
        _previewQty: outQtyRes.value,
        _previewUnit: outUnit,
        _previewPepUso:
          typeRes.value === 'producto_en_proceso'
            ? fabricarSubrecetaDesdePt
              ? 'Subreceta automática'
              : 'Guía PEP'
            : '—',
      };
      colsPreview.tipoSalida = outputConfig._previewTypeLabel;
      colsPreview.cantidadSalida = String(outputConfig._previewQty);
      colsPreview.unidadSalida = outputConfig._previewUnit;
      colsPreview.usoEnGuiaPt = outputConfig._previewPepUso;
    }

    const resolved = resolveInsumoProduct(line.insumo, insumoLookup);
    if (resolved.error) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: resolved.error,
      });
      continue;
    }
    const prod = resolved.product;

    const uom = resolveRecipeIngredientUnitCode(prod.unit_of_measure, ingredientUnits) || 'KG';

    const groupKey = `${name.trim().toLowerCase()}::f${fam.id}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        key: groupKey,
        name: name.trim(),
        receta_family_id: fam.id,
        is_active: line.effectiveActive,
        outputConfig,
        firstRow: line.excelRow,
        items: [],
        activeSourceRow: line.excelRow,
      });
    } else {
      const g = groups.get(groupKey);
      if (g.is_active !== line.effectiveActive) {
        previewRows.push({
          excelRow: line.excelRow,
          status: 'invalid',
          cols: colsPreview,
          issue:
            '«Activa» no coincide con otras filas del mismo nombre y familia; unifique el valor o use solo la primera fila del bloque.',
        });
        continue;
      }
      if (includeOutputConfig && outputConfig && g.outputConfig) {
        const sameOutput =
          g.outputConfig.output_product_type === outputConfig.output_product_type &&
          Number(g.outputConfig.output_quantity) === Number(outputConfig.output_quantity) &&
          String(g.outputConfig.output_unit_of_measure).toLowerCase() ===
            String(outputConfig.output_unit_of_measure).toLowerCase() &&
          (g.outputConfig.output_product_type !== 'producto_en_proceso' ||
            Boolean(g.outputConfig.fabricar_subreceta_desde_pt) ===
              Boolean(outputConfig.fabricar_subreceta_desde_pt));
        if (!sameOutput) {
          previewRows.push({
            excelRow: line.excelRow,
            status: 'invalid',
            cols: colsPreview,
            issue:
              'La configuración de salida (tipo, cantidad, unidad o uso en guía PT) no coincide con otras filas de la misma receta.',
          });
          continue;
        }
      }
    }

    const g = groups.get(groupKey);
    const dup = g.items.some((it) => Number(it.product_id) === Number(prod.id));
    if (dup) {
      previewRows.push({
        excelRow: line.excelRow,
        status: 'invalid',
        cols: colsPreview,
        issue: 'Este insumo ya está en la misma receta (fila duplicada).',
      });
      continue;
    }

    g.items.push({
      product_id: Number(prod.id),
      quantity: qtyRes.value,
      unit_of_measure: uom,
    });

    previewRows.push({
      excelRow: line.excelRow,
      status: 'valid',
      cols: colsPreview,
      issue: '',
    });
  }

  const validRecipes = [];
  for (const g of groups.values()) {
    if (!g.items.length) continue;
    const payload = {
      name: g.name.slice(0, 200),
      receta_family_id: g.receta_family_id,
      is_active: g.is_active,
      items: g.items,
    };
    if (includeOutputConfig && g.outputConfig) {
      payload.output_product_type = g.outputConfig.output_product_type;
      payload.output_quantity = g.outputConfig.output_quantity;
      payload.output_unit_of_measure = g.outputConfig.output_unit_of_measure;
      if (g.outputConfig.output_product_type === 'producto_en_proceso') {
        payload.fabricar_subreceta_desde_pt = g.outputConfig.fabricar_subreceta_desde_pt;
      }
    }
    validRecipes.push({
      displayName: g.name,
      firstRow: g.firstRow,
      payload,
    });
  }

  validRecipes.sort((a, b) => a.firstRow - b.firstRow);

  const validIngredientRows = previewRows.filter((x) => x.status === 'valid').length;
  const invalidCount = previewRows.filter((x) => x.status === 'invalid').length;
  const emptyCount = previewRows.filter((x) => x.status === 'empty').length;

  logRecetasImport('analyze:resumen', {
    recetasValidas: validRecipes.length,
    filasValidas: validIngredientRows,
    filasInvalidas: invalidCount,
    filasVacias: emptyCount,
    erroresInsumo: previewRows
      .filter((r) => r.status === 'invalid' && isInsumoRelatedImportError(r.issue))
      .map((r) => ({ fila: r.excelRow, insumo: r.cols?.insumo, issue: r.issue })),
  });

  return {
    success: true,
    includeOutputConfig,
    previewRows,
    validRecipes,
    recipeCount: validRecipes.length,
    ingredientLines: validIngredientRows,
    validIngredientRows,
    emptyCount,
    invalidCount,
  };
}

/**
 * @param {{ displayName: string; firstRow: number; payload: object }[]} validRecipes
 * @param {{ create: (p: object) => Promise<any> }} svc
 */
export async function executeRecetasImport(validRecipes, svc) {
  const result = { created: 0, errors: [] };
  for (const rec of validRecipes) {
    try {
      const res = await svc.create(rec.payload);
      if (res.success) {
        result.created += 1;
      } else {
        result.errors.push({
          name: rec.displayName,
          row: rec.firstRow,
          message: res.message || 'Respuesta sin éxito',
        });
      }
    } catch (e) {
      result.errors.push({
        name: rec.displayName,
        row: rec.firstRow,
        message: formatApiError(e),
      });
    }
  }
  return result;
}

export function summarizeRecetasImportExec(exec) {
  const { created = 0, errors = [] } = exec || {};
  if (!errors.length) return `Se importaron ${created} receta(s).`;
  const errPart = errors.map((e) => `«${e.name}»: ${e.message}`).join(' · ');
  return `Creadas: ${created}. Errores: ${errors.length}. ${errPart}`;
}

/** Fila con error de búsqueda o unicidad de insumo en el importador. */
function isInsumoRelatedImportError(issue) {
  const s = (issue || '').toLowerCase();
  return (
    s.includes('insumo no encontrado') ||
    s.includes('indique insumo') ||
    s.includes('varios insumos con el mismo nombre')
  );
}

const ERROR_REPORT_HEADER_BASE = [
  'Fila (Excel)',
  'Familia',
  'Nombre receta',
  'Insumo (texto en archivo)',
  'Cantidad',
  'Activa',
  'Detalle del error',
];

const ERROR_REPORT_HEADER_OUTPUT = [
  'Fila (Excel)',
  'Familia',
  'Nombre receta',
  'Tipo producto salida',
  'Cantidad salida',
  'Unidad salida',
  'Insumo (texto en archivo)',
  'Cantidad ingrediente',
  'Activa',
  'Detalle del error',
];

/**
 * Descarga un .xlsx con hojas separadas: insumos no resueltos y otros errores (familia, cantidad, etc.).
 * @param {Array<{ excelRow: number, cols?: object, issue?: string, status: string }>} previewRows
 */
export async function downloadRecetasImportErrorsReport(previewRows, options = {}) {
  const { includeOutputConfig = false } = options;
  const invalid = (previewRows || []).filter((r) => r.status === 'invalid');
  if (invalid.length === 0) return;

  const insumoRows = invalid.filter((r) => isInsumoRelatedImportError(r.issue));
  const otroRows = invalid.filter((r) => !isInsumoRelatedImportError(r.issue));

  const wb = new ExcelJS.Workbook();
  const headers = includeOutputConfig ? ERROR_REPORT_HEADER_OUTPUT : ERROR_REPORT_HEADER_BASE;

  function colVal(v) {
    if (v == null || v === '—' || v === '') return '';
    return String(v);
  }

  function fillSheet(ws, titleText, rows) {
    const n = headers.length;
    const last = String.fromCharCode('A'.charCodeAt(0) + n - 1);
    ws.mergeCells(`A1:${last}1`);
    ws.getCell('A1').value = titleText;
    ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
    ws.getRow(1).height = 44;
    styleHeaderRow(ws.getRow(2), headers);
    ws.columns = includeOutputConfig
      ? [
          { width: 10 },
          { width: 28 },
          { width: 28 },
          { width: 22 },
          { width: 14 },
          { width: 12 },
          { width: 34 },
          { width: 12 },
          { width: 10 },
          { width: 64 },
        ]
      : [
          { width: 10 },
          { width: 28 },
          { width: 28 },
          { width: 34 },
          { width: 12 },
          { width: 10 },
          { width: 64 },
        ];
    if (!rows.length) {
      ws.getCell('A3').value = 'No hay filas en esta categoría.';
    } else {
      const sorted = [...rows].sort((a, b) => a.excelRow - b.excelRow);
      sorted.forEach((r, i) => {
        const out = ws.getRow(3 + i);
        const c = r.cols || {};
        out.getCell(1).value = r.excelRow;
        out.getCell(2).value = colVal(c.familia);
        out.getCell(3).value = colVal(c.nombre);
        if (includeOutputConfig) {
          out.getCell(4).value = colVal(c.tipoSalida);
          out.getCell(5).value = colVal(c.cantidadSalida);
          out.getCell(6).value = colVal(c.unidadSalida);
          out.getCell(7).value = colVal(c.insumo);
          out.getCell(8).value = colVal(c.cantidad);
          out.getCell(9).value = colVal(c.activa);
          out.getCell(10).value = r.issue || '';
        } else {
          out.getCell(4).value = colVal(c.insumo);
          out.getCell(5).value = colVal(c.cantidad);
          out.getCell(6).value = colVal(c.activa);
          out.getCell(7).value = r.issue || '';
        }
      });
    }
  }

  if (insumoRows.length) {
    const w = wb.addWorksheet('Insumos no encontrados', { views: [{ state: 'frozen', ySplit: 2 }] });
    fillSheet(
      w,
      'Insumo no coincide con un producto insumo activo. Verifique el SKU o el nombre exacto, cree o active el producto en Productos, y corrija el Excel o reimporte.',
      insumoRows
    );
  }

  if (otroRows.length) {
    const name = insumoRows.length > 0 ? 'Otros errores' : 'Errores importación recetas';
    const w = wb.addWorksheet(name, { views: [{ state: 'frozen', ySplit: 2 }] });
    fillSheet(
      w,
      'Revisar familia, cantidad, duplicado de insumo en la receta, columna activa, etc. Corrija en el Excel original e importe de nuevo.',
      otroRows
    );
  }

  const buf = await wb.xlsx.writeBuffer();
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const filename = `reporte_errores_recetas_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(
    d.getHours()
  )}${pad(d.getMinutes())}.xlsx`;
  downloadBuffer(buf, filename);
}
